import {
  Box,
  Checkbox,
  IconButton,
  List,
  ListItemText,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { styles } from './styles';
import { AppError } from '@/utils/AppError';
import { CreditCardDTO } from '@/data/DTOs/schedule/CreditCardDTO';
import { useCreditCardService } from '@/data/services/useCreditCardService';
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import Button from '@/components/button/Button';
import { useTranslation } from 'react-i18next';
import CustomPopup from '@/components/customPopup/CustomPopup';
import emptyState from '@/assets/scheduling/empty_credit_card.svg';
import { RegisterCreditCardPopUp } from './registerCreditCard/RegisterCreditCard';

export interface CreditCardListInterface {
  selectedCreditCard?: CreditCardDTO | undefined;
  setSelectedCreditCard: (creditCardId: CreditCardDTO) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  setShowWarning: (showWarning: boolean) => void;
  setWarningMessage: (warningMessage: string) => void;
  onChangeInternalStep: (change: boolean) => void;
  locationId: any;
  appointmentTypeId: string;
}

export const CreditCardList = ({
  selectedCreditCard,
  setSelectedCreditCard,
  loading,
  setLoading,
  setShowWarning,
  setWarningMessage,
  onChangeInternalStep,
  locationId,
  appointmentTypeId,
}: CreditCardListInterface) => {
  const { t } = useTranslation(['creditCard', 'common']);
  const [creditCards, setCreditCards] = useState<CreditCardDTO[]>();
  const [creditCard, setCreditCard] = useState<CreditCardDTO>();
  const [deletedCreditCard, setDeletedCreditCard] =
    useState<CreditCardDTO | null>();
  const [showPopUp, setShowPopUp] = useState(false);
  const { getCreditCardsList, deleteCreditCard } = useCreditCardService();
  const [showRegister, setShowRegister] = useState(false);

  useEffect(() => {
    fetchCreditCards(true);
  }, []);

  const fetchCreditCards = async (
    useFirst: boolean = false,
    creditCard?: CreditCardDTO
  ) => {
    setLoading(true);
    setShowWarning(false);
    getCreditCardsList(locationId, appointmentTypeId)
      .then((creditCards) => {
        if (selectedCreditCard && !useFirst) {
          const selected = creditCards.find((creditCardReceived) => {
            return creditCard
              ? creditCard?.creditCardNumber?.slice(-4) ==
                  creditCardReceived.lastDigits
              : selectedCreditCard?.creditCardId ==
                  creditCardReceived.creditCardId;
          });
          if (selected) setCreditCard(selected);
        } else if (creditCards.length > 0) {
          setCreditCard(creditCards[0]);
        }
        setCreditCards(creditCards);
      })
      .catch((error: AppError) => {
        setWarningMessage(error.message);
        setShowWarning(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const removeCreditCard = async () => {
    setLoading(true);
    setShowWarning(false);
    deleteCreditCard(deletedCreditCard?.creditCardId ?? '')
      .then(() => {
        setDeletedCreditCard(null);
        if (creditCards && creditCards?.length > 0) {
          setSelectedCreditCard(creditCards[0]);
          fetchCreditCards(true);
        } else {
          fetchCreditCards();
        }
      })
      .catch((error: AppError) => {
        setWarningMessage(error.message);
        setShowWarning(true);
      })
      .finally(() => {
        setShowPopUp(false);
      });
  };

  const item = (creditCardItem: CreditCardDTO, index: number) => {
    return (
      <Box key={index} sx={styles.paymentCard}>
        <Box
          sx={styles.paymentCard}
          onClick={() => {
            setCreditCard(creditCardItem);
          }}
        >
          <Checkbox
            checked={creditCard == creditCardItem}
            icon={<RadioButtonUnchecked sx={styles.checkButton} />}
            checkedIcon={<RadioButtonChecked sx={styles.checkButton} />}
          />
          <ListItemText
            sx={styles.listText}
            primary={creditCardItem.banner}
            secondary={
              <Typography sx={styles.description}>
                {`${creditCardItem.banner} **** ${creditCardItem.lastDigits}`}
              </Typography>
            }
          />
        </Box>
        <IconButton
          onClick={() => {
            setDeletedCreditCard(creditCardItem);
            setShowPopUp(true);
          }}
        >
          <Delete sx={styles.trashIcon} />
        </IconButton>
      </Box>
    );
  };

  const emptyStateView = (
    <Box sx={styles.emptyState}>
      <Box component="img" src={emptyState} sx={styles.emptyStateImage} />
      <Typography sx={styles.titleEmpty}>
        {t('screen_credit_card_empty_state_title')}
      </Typography>
      <Typography sx={styles.descriptionEmpty}>
        {t('screen_credit_card_empty_state_description')}
      </Typography>
    </Box>
  );

  return (
    <Box sx={styles.content}>
      {creditCards?.length == 0 ? (
        emptyStateView
      ) : (
        <List sx={styles.list}>
          {creditCards?.map((creditCardItem: CreditCardDTO, index) =>
            item(creditCardItem, index)
          )}
        </List>
      )}
      <Box sx={styles.buttonBox}>
        <Button
          title={t('common:confirm')}
          onClick={() => {
            if (creditCard) setSelectedCreditCard(creditCard);
            onChangeInternalStep(false);
          }}
          disable={
            loading ||
            creditCard == undefined ||
            creditCard == null ||
            creditCards?.length == 0
          }
          loading={loading}
        />
        <Button
          title={t('screen_credit_card_register_new')}
          outlined
          onClick={() => {
            setShowRegister(true);
          }}
          disable={loading}
        />
      </Box>
      <CustomPopup
        title={t('screen_credit_card_delete_popup_title')}
        description={t('screen_credit_card_delete_popup_description')}
        popupHidden={!showPopUp}
        imageHidden={true}
        closeHidden={true}
        isDisabled={loading}
        closeAction={() => setShowPopUp(false)}
        buttonTitleArray={[t('cancel'), t('confirm')]}
        buttonOnlyTextArray={[true, false]}
        buttonOutlinedArray={[true, false]}
        buttonColorArray={['inherit', 'error']}
        buttonActionArray={[
          () => setShowPopUp(false),
          () => removeCreditCard(),
        ]}
      />
      <RegisterCreditCardPopUp
        showPopUp={showRegister}
        setShowPopUp={setShowRegister}
        setCreditCard={(registeredCreditCard) => {
          setSelectedCreditCard(registeredCreditCard);
          fetchCreditCards(false, registeredCreditCard);
        }}
        appointmentTypeId={appointmentTypeId}
        locationId={locationId}
      />
    </Box>
  );
};
